// Canonical reporting-requirement contract, mirrored from the backend service
// of the same name (`backend/src/services/reportingRequirements.js`).
//
// The backend resolves the contract once and returns it as
// `reporting_requirements_resolved` on every assignment payload. This module is
// the client-side reader: it prefers that object, and falls back to the same
// merge rules for payloads that predate it (an explicit assignment value wins,
// a missing key inherits the activity type default, and both the `requires_*`
// spelling and the legacy short key are understood).

export type RequirementSource = 'assignment' | 'type' | 'template' | 'none';

export interface ReportingRequirementsContract {
    version?: number;
    requires_input?: boolean;
    requires_photo?: boolean;
    requires_gps?: boolean;
    requires_start_time?: boolean;
    requires_end_time?: boolean;
    requires_area_completed?: boolean;
    requires_materials?: boolean;
    requires_equipment?: boolean;
    requires_weather?: boolean;
    requires_observations?: boolean;
    requires_before_photo?: boolean;
    requires_after_photo?: boolean;
    requires_signature?: boolean;
    requires_completion_notes?: boolean;
    requires_quantity?: boolean;
    operation_template?: {
        key: string;
        label: string;
        planning_fields: string[];
        quantity_units: string[];
        work_prompt: string;
        safety_guidance: string;
        completion_criteria: string[];
    };
    min_inspection_points?: number | null;
    sources?: Record<string, RequirementSource | string>;
    overrides?: Record<string, unknown>;
}

/** Short names the mobile manager form used to write. */
export const LEGACY_REQUIREMENT_KEYS: Record<string, string> = {
    before_photo: 'requires_before_photo',
    after_photo: 'requires_after_photo',
    during_photo: 'requires_photo',
    observations: 'requires_observations',
    gps: 'requires_gps',
    weather: 'requires_weather',
    completion_notes: 'requires_completion_notes',
    require_signature: 'requires_signature',
    signature: 'requires_signature',
};

/** Flags the assignment editor can pin for one job. */
export const EDITABLE_REQUIREMENT_FLAGS = [
    'requires_before_photo',
    'requires_after_photo',
    'requires_observations',
    'requires_signature',
] as const;

export const REQUIREMENT_LABELS: Record<string, string> = {
    requires_before_photo: 'Before-activity photo',
    requires_after_photo: 'After-activity photo',
    requires_photo: 'Photo during work',
    requires_observations: 'Sample-point observations',
    requires_gps: 'GPS location',
    requires_weather: 'Weather capture',
    requires_signature: 'Farmer confirmation',
    requires_area_completed: 'Area completed',
    requires_start_time: 'Start time',
    requires_end_time: 'End time',
    requires_materials: 'Materials',
    requires_equipment: 'Equipment',
    requires_input: 'Inputs',
    requires_completion_notes: 'Completion notes',
    requires_quantity: 'Completed quantity and unit',
};

export const parseRequirements = (value: unknown): Record<string, any> => {
    if (!value) return {};
    if (typeof value === 'string') {
        try {
            const parsed = JSON.parse(value);
            return parsed && typeof parsed === 'object' ? parsed : {};
        } catch {
            return {};
        }
    }
    return typeof value === 'object' ? (value as Record<string, any>) : {};
};

const toIntOrNull = (value: unknown): number | null => {
    if (value === null || value === undefined || value === '') return null;
    const parsed = Number(value);
    return Number.isInteger(parsed) ? parsed : null;
};

const toIntOrUndefined = (value: unknown): number | undefined => {
    const parsed = toIntOrNull(value);
    return parsed === null || parsed < 0 ? undefined : parsed;
};

/** The requirement JSON exactly as the assignment stores it. */
export const storedRequirements = (assignment: any): Record<string, any> =>
    parseRequirements(assignment?.reporting_requirements);

/** True when the manager pinned this requirement on the assignment itself. */
export const isRequirementOverridden = (assignment: any, key: string): boolean => {
    const resolved = assignment?.reporting_requirements_resolved;
    if (resolved && typeof resolved === 'object') {
        return Boolean(resolved.overrides && Object.prototype.hasOwnProperty.call(resolved.overrides, key));
    }
    const stored = storedRequirements(assignment);
    if (Object.prototype.hasOwnProperty.call(stored, key)) return true;
    return Object.entries(LEGACY_REQUIREMENT_KEYS).some(
        ([legacy, canonical]) => canonical === key && Object.prototype.hasOwnProperty.call(stored, legacy),
    );
};

/** Where a resolved requirement came from, for the "inherited / overridden" hint. */
export const requirementSource = (assignment: any, key: string): RequirementSource => {
    const resolved = assignment?.reporting_requirements_resolved;
    const source = resolved?.sources?.[key];
    if (source === 'assignment' || source === 'type' || source === 'none') return source;
    if (isRequirementOverridden(assignment, key)) return 'assignment';
    const typeValue = assignment?.[`type_${key}`] ?? assignment?.[key];
    return typeValue === undefined || typeValue === null ? 'none' : 'type';
};

/**
 * Resolve the effective contract for one assignment payload, preferring the
 * object the backend resolved and falling back to the merge rules above.
 */
export const resolveReportingRequirements = (assignment: any): ReportingRequirementsContract => {
    const resolved = assignment?.reporting_requirements_resolved;
    if (resolved && typeof resolved === 'object') return resolved as ReportingRequirementsContract;

    const stored = storedRequirements(assignment);
    const contract: ReportingRequirementsContract = {};
    const sources: Record<string, RequirementSource> = {};
    const overrides: Record<string, unknown> = {};

    const read = (key: string): boolean => {
        if (Object.prototype.hasOwnProperty.call(stored, key)) {
            sources[key] = 'assignment';
            overrides[key] = stored[key] === true;
            return stored[key] === true;
        }
        const legacy = Object.entries(LEGACY_REQUIREMENT_KEYS).find(([, canonical]) => canonical === key);
        if (legacy && Object.prototype.hasOwnProperty.call(stored, legacy[0])) {
            sources[key] = 'assignment';
            overrides[key] = stored[legacy[0]] === true;
            return stored[legacy[0]] === true;
        }
        const typeValue = assignment?.[`type_${key}`] ?? assignment?.[key];
        sources[key] = typeValue === undefined || typeValue === null ? 'none' : 'type';
        return typeValue === true;
    };

    Object.keys(REQUIREMENT_LABELS).forEach((key) => {
        (contract as any)[key] = read(key);
    });

    const assignmentMin = toIntOrUndefined(stored.min_inspection_points)
        ?? toIntOrUndefined(assignment?.assignment_min_inspection_points)
        ?? toIntOrUndefined(assignment?.min_inspection_points);
    const typeMin = toIntOrUndefined(assignment?.type_min_inspection_points);
    contract.min_inspection_points = assignmentMin ?? typeMin ?? null;
    sources.min_inspection_points = assignmentMin !== undefined ? 'assignment' : typeMin !== undefined ? 'type' : 'none';
    if (assignmentMin !== undefined) overrides.min_inspection_points = assignmentMin;

    contract.sources = sources;
    contract.overrides = overrides;
    return contract;
};

/** Human-readable evidence list, in the order it gets collected. */
export const requirementEvidenceLabels = (contract: ReportingRequirementsContract): string[] => {
    const labels: string[] = [];
    if (contract.requires_before_photo) labels.push(REQUIREMENT_LABELS.requires_before_photo);
    if (contract.requires_photo) labels.push(REQUIREMENT_LABELS.requires_photo);
    if (contract.requires_after_photo) labels.push(REQUIREMENT_LABELS.requires_after_photo);
    if (contract.requires_observations) labels.push(REQUIREMENT_LABELS.requires_observations);
    const minPoints = Number(contract.min_inspection_points) || 0;
    if (minPoints > 0) labels.push(`${minPoints} inspection points`);
    if (contract.requires_gps) labels.push(REQUIREMENT_LABELS.requires_gps);
    if (contract.requires_weather) labels.push(REQUIREMENT_LABELS.requires_weather);
    if (contract.requires_area_completed) labels.push(REQUIREMENT_LABELS.requires_area_completed);
    if (contract.requires_start_time) labels.push(REQUIREMENT_LABELS.requires_start_time);
    if (contract.requires_end_time) labels.push(REQUIREMENT_LABELS.requires_end_time);
    if (contract.requires_materials) labels.push(REQUIREMENT_LABELS.requires_materials);
    if (contract.requires_input) labels.push(REQUIREMENT_LABELS.requires_input);
    if (contract.requires_equipment) labels.push(REQUIREMENT_LABELS.requires_equipment);
    if (contract.requires_completion_notes) labels.push(REQUIREMENT_LABELS.requires_completion_notes);
    if (contract.requires_quantity) labels.push(REQUIREMENT_LABELS.requires_quantity);
    if (contract.requires_input) labels.push(REQUIREMENT_LABELS.requires_input);
    if (contract.requires_signature) labels.push(REQUIREMENT_LABELS.requires_signature);
    return labels;
};

/**
 * The payload to send for the editable flags: only what the manager changed is
 * included, so an untouched requirement keeps inheriting the activity type
 * default instead of being pinned at creation time.
 */
export const buildRequirementOverrides = (
    values: Partial<Record<(typeof EDITABLE_REQUIREMENT_FLAGS)[number], boolean | null>>,
): Record<string, boolean> => {
    const payload: Record<string, boolean> = {};
    EDITABLE_REQUIREMENT_FLAGS.forEach((key) => {
        const value = values[key];
        if (typeof value === 'boolean') payload[key] = value;
    });
    return payload;
};
